import React from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, PackageOpen, ShieldCheck, Tag } from "lucide-react";
import BBBBadge from "@/components/BBBBadge";

/**
 * Prominent "Get a Free Quote" call-to-action strip.
 * Sits at the very top of the main content area on every marketing page.
 * Communicates: 24/7 availability, package-deal reminder, and dual CTAs
 * (phone + scroll-to-form).
 *
 * Below the pill: a slim Trust strip (Licensed & Insured + BBB + WELCOME15).
 * Placing the trust info here (not inside the fixed nav) keeps the mobile
 * top nav compact and prevents the fixed header from clipping the pill.
 */
export default function QuoteCTA() {
  return (
    <section
      data-testid="quote-cta-top"
      className="pt-24 md:pt-28 pb-4"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 border border-electric-500/50 shadow-xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(46,124,224,0.35),transparent_55%)] pointer-events-none" />

          <div className="relative p-4 md:p-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
            {/* Copy */}
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                <Clock className="w-3 h-3" />
                24 / 7 Availability
              </div>
              <div
                className="flex items-start gap-2 text-sm md:text-base text-white leading-snug"
                data-testid="package-deal-reminder"
              >
                <PackageOpen className="w-4 h-4 flex-shrink-0 text-white/80 mt-0.5" />
                <span>
                  <span className="font-bold">Ask about package deals</span> &mdash;
                  bundle any two services for <span className="font-bold">10% off</span> the equal-or-lesser add-on.
                </span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex gap-2 min-w-0">
              <a
                href="tel:+16155192629"
                data-testid="quote-cta-phone"
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-brand-700 font-bold px-4 py-2.5 text-sm hover:bg-stone-100 transition-colors shadow"
              >
                <Phone className="w-3.5 h-3.5" />
                Call (615) 519-2629
              </a>
              <a
                href="#quote"
                data-testid="quote-cta-book"
                className="group inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-900/40 hover:bg-brand-900/60 text-white font-bold px-4 py-2.5 text-sm border border-white/25 backdrop-blur-sm transition-colors"
              >
                Request a Quote
              </a>
            </div>
          </div>
        </div>

        {/* Trust strip — sits BENEATH the 24/7 pill so it's always readable
            on mobile and never clipped by the fixed top nav. */}
        <div
          data-testid="trust-strip-under-cta"
          className="mt-3 flex flex-wrap items-center justify-center md:justify-between gap-2 md:gap-3"
        >
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <div
              data-testid="licensed-insured-badge"
              className="inline-flex items-center gap-2 rounded-full bg-brand-50 border border-brand-100 px-3 py-1.5 text-brand-800 text-xs md:text-sm font-bold"
            >
              <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 text-brand-600" />
              <span>Licensed &amp; Insured</span>
            </div>
            <BBBBadge size="sm" />
          </div>
          <Link
            to="/pressure-washing-estimate-calculator/"
            data-testid="new-customer-promo-badge"
            className="inline-flex items-center gap-2 rounded-full bg-electric-500 hover:bg-electric-400 text-white text-xs md:text-sm font-bold px-3.5 py-1.5 shadow-md transition-all hover:-translate-y-0.5"
          >
            <Tag className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
            <span>
              <span className="font-extrabold">15% OFF</span> new customers &mdash;{" "}
              <span className="tracking-wide">WELCOME15</span>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
